// ScatterDemogsChart.jsx
import {  useCallback,useState, useMemo, useRef, useEffect,useLayoutEffect } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  ResponsiveContainer,
  Label,
} from "recharts";
import { Box, ButtonBase } from "@mui/material";
import { motion, LayoutGroup } from "framer-motion";

const GROUP_COLOURS = {
  Age: "#1f77b4",
  Education: "#ff7f0e",
  Gender: "#d62728",
  Ethnicity: "#2ca02c",
  Electorate: "#c7c7c7",
};

const party_name_lookup = {
  coalition: "coalition",
  act_pred_n: "act",
  nat_pred_n: "nat",
  nzf_pred_n: "nzf",
  lab_pred_n: "lab",
  green_pred_n: "green",
  tpm_pred_n: "tpm",
};

function getGroup(type_code) {
  if (type_code === "Age") return "Age";
  if (type_code === "Education") return "Education";
  if (type_code === "Gender") return "Gender";
  if (type_code === "Ethnicity") return "Ethnicity";
  if (type_code === "Electorate") return "Electorate";
  return null;
}

// These must match the props passed to XAxis / YAxis below
const CHART_HEIGHT = 500;
const CHART_MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
const Y_AXIS_WIDTH = 50;
const X_AXIS_HEIGHT = 30;

const VIEW_MODES = [
  { id: "latest", label: "Latest Wave" },
  { id: "movement", label: "Movement Over Time" },
];

export default function ScatterDemogsChart({ scatterDemogsRows, selectedParty, waveOptions }) {
  const [hovered, setHovered] = useState(null);
  const [viewMode, setViewMode] = useState("latest");
  const [containerWidth, setContainerWidth] = useState(0);
  const observerRef = useRef(null);

  const containerRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) return;

    const updateWidth = () => {
      const width = node.getBoundingClientRect().width;
      setContainerWidth(width);
    };

    updateWidth();

    observerRef.current = new ResizeObserver(() => {
      updateWidth();
    });

    observerRef.current.observe(node);
  }, []);


  const fixed_selectedParty = selectedParty ? party_name_lookup[selectedParty] : null;
  const partyKey = fixed_selectedParty ? fixed_selectedParty.toLowerCase() : "";

  const sortedWaves = [...(waveOptions || [])].sort((a, b) => a - b);
  const latestWave = sortedWaves[sortedWaves.length - 1];

  const allData = useMemo(() => {
    if (!scatterDemogsRows || !partyKey) return [];
    
    const filtered = scatterDemogsRows.filter((row) => row.type !== "Vote 2023");
    // Build a lookup: wave+type_name → row, so we can find the previous wave's value
    const byWaveName = {};
    filtered.forEach((row) => {
      byWaveName[`${row.wave}__${row.type_name}`] = row;
    });
    const waves = [...new Set(filtered.map((r) => r.wave))].sort((a, b) => Number(a) - Number(b));
    return filtered.map((row) => {
      const waveIdx = waves.indexOf(row.wave);
      const prevWave = waveIdx > 0 ? waves[waveIdx - 1] : null;
      const prevRow = prevWave ? byWaveName[`${prevWave}__${row.type_name}`] : null;
      const changeFromPrev = prevRow
        ? (row[`${partyKey}_vi`] - prevRow[`${partyKey}_vi`]) * 100
        : null;
      return {
        x: row[`${partyKey}_2023`] * 100,
        y: row[`${partyKey}_vi`] * 100,
        name: row.type_name,
        group: getGroup(row.type),
        wave: row.wave,
        changelabel: (row[`${partyKey}_vi`] * 100 || 0) - (row[`${partyKey}_2023`] * 100 || 0),
        changeFromPrev,
        size: Math.abs((row[`${partyKey}_vi`] || 0) - (row[`${partyKey}_2023`] || 0)) / 100,
      };
    });
  }, [scatterDemogsRows, partyKey]);

  const latestData = useMemo(
    () => allData.filter((d) => Number(d.wave) === Number(latestWave)),
    [allData, latestWave]
  );

  const waveOpacity = useMemo(() => {
    const map = {};
    sortedWaves.forEach((w, i) => {
      map[w] = 0.2 + (0.8 * i) / Math.max(sortedWaves.length - 1, 1);
    });
    return map;
  }, [sortedWaves]);

  const displayData = viewMode === "latest" ? latestData : allData;

  // Compute the same domains that Recharts will use for the axes
  const xDomain = useMemo(() => {
    const xs = allData.map((d) => d.x).filter(isFinite);
    if (!xs.length) return [0, 100];
    return [Math.min(...xs) * 0.9, Math.max(...xs) * 1.1];
  }, [allData]);

  const yDomain = useMemo(() => {
    const ys = allData.map((d) => d.y).filter(isFinite);
    if (!ys.length) return [0, 100];
    return [Math.min(...ys) * 0.9, Math.max(...ys) * 1.1];
  }, [allData]);

  // Convert data coords → pixel coords in the overlay SVG
  const toPixel = (dataX, dataY) => {
    const plotLeft  = CHART_MARGIN.left + Y_AXIS_WIDTH;
    const plotRight = containerWidth - CHART_MARGIN.right;
    const plotTop   = CHART_MARGIN.top;
    const plotBottom = CHART_HEIGHT - CHART_MARGIN.bottom - X_AXIS_HEIGHT;
    const px = plotLeft + ((dataX - xDomain[0]) / (xDomain[1] - xDomain[0])) * (plotRight - plotLeft);
    const py = plotBottom - ((dataY - yDomain[0]) / (yDomain[1] - yDomain[0])) * (plotBottom - plotTop);
    return [px, py];
  };

  // Build arrow segments grouped by demographic name, ordered by wave
  const arrowSegments = useMemo(() => {
    if (viewMode !== "movement" || containerWidth === 0 || !allData.length) return [];
    const byName = {};
    allData.forEach((d) => {
      if (!byName[d.name]) byName[d.name] = [];
      byName[d.name].push(d);
    });

    const segs = [];
    Object.values(byName).forEach((points) => {
      const sorted = [...points].sort((a, b) => Number(a.wave) - Number(b.wave));
      for (let i = 0; i < sorted.length - 1; i++) {
        const p = sorted[i];
        const n = sorted[i + 1];
        const [x1, y1] = toPixel(p.x, p.y);
        const [x2raw, y2raw] = toPixel(n.x, n.y);
        const dx = x2raw - x1;
        const dy = y2raw - y1;
        const len = Math.hypot(dx, dy);
        if (len < 2) continue;
        // Trim both ends by the dot radius so the line runs between dot edges
        const DOT_R = 7;
        const ux = dx / len;
        const uy = dy / len;
        const x1t = x1 + ux * DOT_R;
        const y1t = y1 + uy * DOT_R;
        const x2t = x2raw - ux * DOT_R;
        const y2t = y2raw - uy * DOT_R;
        if (Math.hypot(x2t - x1t, y2t - y1t) < 2) continue;
        segs.push({ x1: x1t, y1: y1t, x2: x2t, y2: y2t, group: p.group, key: `${p.name}-${i}` });
      }
    });
    return segs;
  // toPixel is stable given its deps — include the real deps explicitly
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, allData, containerWidth, xDomain, yDomain]);

  const renderDot = (props) => {
    const { cx, cy, payload } = props;
    const opacity = viewMode === "movement" ? (waveOpacity[payload.wave] ?? 1) : 1;
    const r = Math.max(4, Math.min(10, (payload.size || 0.01) * 800 + 4));
    return (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={GROUP_COLOURS[payload.group]}
        fillOpacity={opacity}
        stroke={GROUP_COLOURS[payload.group]}
        strokeOpacity={opacity * 0.6}
        strokeWidth={1}
        onMouseEnter={() => setHovered({ cx, cy, payload })}
        onMouseLeave={() => setHovered(null)}
        style={{ cursor: "pointer" }}
      />
    );
  };

  if (!scatterDemogsRows || !selectedParty) return null;


  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* ── View-mode pill selector ── */}
      <Box
        sx={{
          display: "flex",
          gap: 0,
          p: 0,
          borderRadius: 99,
          bgcolor: "rgba(0,0,0,0.04)",
          mt: 5,
          mb: 1,
          width: "fit-content",
          mx: "auto",
        }}
      >
        <LayoutGroup id="scatter-view-pills">
          {VIEW_MODES.map((m) => {
            const isSelected = viewMode === m.id;
            return (
              <ButtonBase
                key={m.id}
                disableRipple
                onClick={() => setViewMode(m.id)}
                sx={{
                  position: "relative",
                  px: 2,
                  py: 0.65,
                  borderRadius: 99,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: isSelected ? "#1976D2" : "#BDBDBD",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: isSelected ? "#BDBDBD" : "#1976D2",
                    backgroundColor: !isSelected ? "rgba(0,0,0,0.04)" : "transparent",
                  },
                  zIndex: 1,
                }}
              >
                <span style={{ position: "relative", zIndex: 2 }}>{m.label}</span>
                {isSelected && (
                  <Box
                    component={motion.div}
                    layoutId="scatter-active-pill"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      borderRadius: 99,
                      bgcolor: "white",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                      border: "1px solid rgba(0,0,0,0.02)",
                      zIndex: 1,
                    }}
                  />
                )}
              </ButtonBase>
            );
          })}
        </LayoutGroup>
      </Box>

      {/* ── Chart + arrow overlay ── */}
      <div
        ref={containerRef}
        style={{ position: "relative", width: "100%", height: CHART_HEIGHT }}
      >
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <ScatterChart margin={CHART_MARGIN} onMouseLeave={() => setHovered(null)}>
            <CartesianGrid />
            <XAxis
              type="number"
              dataKey="x"
              name="2023 Vote Share"
              unit="%"
              allowDataOverflow
              height={X_AXIS_HEIGHT}
              domain={xDomain}
              tickFormatter={(val) => val.toFixed(0)}
            >
              <Label value="2023 Vote Share" position="insideBottom" offset={-5} />
            </XAxis>
            <YAxis
              type="number"
              dataKey="y"
              name="Predicted Vote Share"
              unit="%"
              allowDataOverflow
              width={Y_AXIS_WIDTH}
              domain={yDomain}
              tickFormatter={(val) => val.toFixed(0)}
            >
              <Label
                value="Predicted Vote Share"
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
              />
            </YAxis>
            <ZAxis type="number" dataKey="size" range={[30, 100]} name="Change" />

            {Object.keys(GROUP_COLOURS).map((group) => (
              <Scatter
                key={group}
                name={group}
                data={displayData.filter((d) => d.group === group)}
                fill={GROUP_COLOURS[group]}
                line={false}
                shape={renderDot}
                dataKey="x"
                isAnimationActive={false}
                onMouseEnter={(e) => setHovered(e)}
                onMouseLeave={() => setHovered(null)}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>

        {/* Absolutely-positioned SVG arrow overlay — drawn in the same coordinate space */}
        {viewMode === "movement" && containerWidth > 0 && (
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: containerWidth,
              height: CHART_HEIGHT,
              pointerEvents: "none",
              overflow: "visible",
            }}
          >
            <defs>
              {Object.entries(GROUP_COLOURS).map(([group, colour]) => (
                <marker
                  key={group}
                  id={`sarrow-${group}`}
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="5"
                  markerHeight="5"
                  orient="auto"
                >
                  <path d="M 0 2 L 10 5 L 0 8 z" fill={colour} fillOpacity="0.9" />
                </marker>
              ))}
            </defs>
            {arrowSegments.map((s) => (
              <line
                key={s.key}
                x1={s.x1}
                y1={s.y1}
                x2={s.x2}
                y2={s.y2}
                stroke={GROUP_COLOURS[s.group] || "#999"}
                strokeWidth="1.5"
                strokeOpacity="0.6"
                markerEnd={`url(#sarrow-${s.group})`}
              />
            ))}
          </svg>
        )}

        {/* Custom hover tooltip */}

        {hovered && (
          <div
            style={{
              position: "absolute",
              left: hovered.cx + 20,
              top: hovered.cy,
              background: "white",
              padding: "8px",
              border: "1px solid #ccc",
              pointerEvents: "none",
              borderRadius: 4,
              zIndex: 10,
            }}
          >
            <strong>{hovered.payload.name}</strong> {hovered.payload.group}
            <div>2023 Vote Share: {hovered.payload.x.toFixed(1)}%</div>
            <div>Predicted: {hovered.payload.y.toFixed(1)}%</div>
            <div>
              vs 2023:{" "}
              <span style={{ color: hovered.payload.changelabel >= 0 ? "green" : "red" }}>
                {hovered.payload.changelabel >= 0 ? "+" : ""}{hovered.payload.changelabel.toFixed(1)}%
              </span>
            </div>
            {hovered.payload.changeFromPrev !== null && (
              <div>
                vs last wave:{" "}
                <span style={{ color: hovered.payload.changeFromPrev >= 0 ? "green" : "red" }}>
                  {hovered.payload.changeFromPrev >= 0 ? "+" : ""}{hovered.payload.changeFromPrev.toFixed(1)}%
                </span>
              </div>
            )}
            <div style={{ color: "#888", fontSize: "0.85em" }}>
              Wave: {hovered.payload.wave}
            </div>
          </div>
        )}
      </div>

      {/* Manual legend — outside the chart so it doesn't affect the plot area */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap", mt: 1 }}>
        {Object.entries(GROUP_COLOURS).map(([group, colour]) => (
          <Box key={group} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: colour }} />
            <span style={{ fontSize: 11 }}>{group}</span>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
